var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, css, html, nothing } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { config } from "./config";
import { createInferenceClient } from "./inference";
import { prepareImage } from "./image/prepare";
import { drawOverlay } from "./overlay/canvas-overlay";
import { classInfo, unknownClassInfo } from "./i18n";
import { findDetectionAt } from "./hit-test";
let PruneItApp = class PruneItApp extends LitElement {
    constructor() {
        super(...arguments);
        this.status = { kind: "idle" };
        this.uiLang = "en";
        this.confidenceThreshold = config.confidenceThreshold;
        this.inspectedDetection = null;
        this.inspectPos = { x: 0, y: 0 };
        this.inference = createInferenceClient();
        this.lastImageBlob = null;
        this.lastFullResult = null;
    }
    static { this.styles = css `
    :host {
      display: block;
      font-family: system-ui, -apple-system, sans-serif;
      max-width: 720px;
      margin: 0 auto;
      padding: env(safe-area-inset-top) env(safe-area-inset-right)
        env(safe-area-inset-bottom) env(safe-area-inset-left);
      color: #1a1a1a;
    }
    header {
      padding: 1rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .header-left {
      display: flex;
      align-items: baseline;
      gap: 0.5rem;
    }
    h1 {
      font-size: 1.25rem;
      margin: 0;
    }
    .version {
      font-size: 0.75rem;
      color: #888;
    }
    .lang-toggle {
      padding: 4px 10px;
      border-radius: 4px;
      border: 1px solid #ccc;
      background: #fff;
      font-size: 0.75rem;
      font-weight: 600;
      cursor: pointer;
      color: #333;
    }
    .lang-toggle:hover {
      background: #f5f5f5;
    }

    /* Legend */
    .legend {
      display: flex;
      gap: 4px;
      padding: 0.5rem 1rem;
      flex-wrap: wrap;
    }
    .legend-item {
      position: relative;
      display: flex;
      align-items: center;
      gap: 5px;
      padding: 4px 10px;
      border-radius: 4px;
      font-size: 0.8rem;
      color: #444;
      cursor: default;
      background: #f7f7f7;
      border: 1px solid #e8e8e8;
    }
    .legend-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      flex-shrink: 0;
    }
    .legend-tooltip {
      display: none;
      position: absolute;
      left: 0;
      top: calc(100% + 6px);
      width: 260px;
      background: #fff;
      border: 1px solid #ddd;
      border-radius: 6px;
      padding: 10px 12px;
      font-size: 0.78rem;
      color: #333;
      line-height: 1.5;
      z-index: 20;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
    .legend-item:hover .legend-tooltip {
      display: block;
    }

    /* Capture button */
    .capture {
      display: block;
      width: calc(100% - 2rem);
      margin: 0.75rem 1rem;
      padding: 0.85rem;
      border-radius: 0.5rem;
      border: 2px solid #2c5e2e;
      background: #2c5e2e;
      color: white;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      text-align: center;
    }
    .capture:active {
      background: #234a25;
    }
    input[type="file"] {
      display: none;
    }

    /* Canvas area */
    .canvas-wrap {
      position: relative;
      margin: 0 1rem;
    }
    canvas {
      display: block;
      max-width: 100%;
      height: auto;
      border-radius: 6px;
    }

    /* Inspect panel (appears on detection click) */
    .inspect-panel {
      position: absolute;
      background: rgba(255, 255, 255, 0.95);
      border: 1px solid #ddd;
      border-radius: 8px;
      padding: 10px 14px;
      font-size: 0.8rem;
      line-height: 1.5;
      max-width: 260px;
      z-index: 30;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
      color: #222;
    }
    .inspect-panel .class-name {
      font-weight: 600;
      font-size: 0.9rem;
      margin-bottom: 4px;
    }
    .inspect-panel .confidence-bar {
      height: 6px;
      border-radius: 3px;
      background: #eee;
      margin: 6px 0;
      overflow: hidden;
    }
    .inspect-panel .confidence-fill {
      height: 100%;
      border-radius: 3px;
    }
    .inspect-panel .desc {
      color: #555;
      margin-top: 6px;
    }
    .inspect-close {
      position: absolute;
      top: 4px;
      right: 8px;
      background: none;
      border: none;
      font-size: 1rem;
      cursor: pointer;
      color: #888;
      padding: 2px;
    }

    /* Confidence slider */
    .slider-row {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 0.5rem 1rem;
    }
    .slider-row label {
      font-size: 0.78rem;
      color: #666;
      white-space: nowrap;
    }
    .slider-row input[type="range"] {
      flex: 1;
      height: 4px;
      accent-color: #2c5e2e;
    }
    .slider-row .slider-val {
      font-size: 0.85rem;
      font-weight: 600;
      min-width: 36px;
      text-align: right;
    }

    /* Stats bar */
    .stats {
      display: flex;
      gap: 8px;
      padding: 0.75rem 1rem;
    }
    .stat {
      flex: 1;
      background: #f7f7f7;
      border: 1px solid #eee;
      border-radius: 6px;
      padding: 8px 10px;
      text-align: center;
    }
    .stat .n {
      font-size: 1.15rem;
      font-weight: 600;
    }
    .stat .l {
      font-size: 0.7rem;
      color: #888;
      margin-top: 2px;
    }

    /* Status line */
    .status {
      padding: 0.5rem 1rem;
      font-size: 0.85rem;
      color: #666;
    }
    .error {
      color: #b91c1c;
    }

    /* Model info */
    .model-info {
      margin: 0.5rem 1rem 1rem;
      border: 1px solid #eee;
      border-radius: 6px;
      font-size: 0.78rem;
    }
    .model-info summary {
      padding: 8px 12px;
      cursor: pointer;
      color: #888;
      list-style: none;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .model-info summary::before {
      content: "";
      display: inline-block;
      width: 0;
      height: 0;
      border-left: 5px solid #aaa;
      border-top: 4px solid transparent;
      border-bottom: 4px solid transparent;
      transition: transform 0.15s;
    }
    .model-info[open] summary::before {
      transform: rotate(90deg);
    }
    .model-detail {
      padding: 0 12px 10px;
    }
    .model-row {
      display: flex;
      justify-content: space-between;
      padding: 3px 0;
      border-bottom: 1px solid #f5f5f5;
    }
    .model-row:last-child {
      border-bottom: none;
    }
    .model-row .mk {
      color: #888;
    }
    .model-row .mv {
      font-weight: 500;
      color: #333;
    }
  `; }
    render() {
        return html `
      <header>
        <div class="header-left">
          <h1>Prune-It</h1>
          <span class="version">v${config.modelVersion}</span>
        </div>
        <button class="lang-toggle" @click=${this.toggleLang}>
          ${this.uiLang === "en" ? "IT" : "EN"}
        </button>
      </header>

      ${this.renderLegend()}

      <label class="capture">
        <input
          type="file"
          accept="image/*"
          capture="environment"
          @change=${this.onFileChange}
        />
        ${this.captureButtonText()}
      </label>

      <div class="canvas-wrap">
        <canvas @click=${this.onCanvasClick}></canvas>
        ${this.renderInspectPanel()}
      </div>

      ${this.renderSlider()}
      ${this.renderStats()}

      <div class="status ${this.status.kind === "error" ? "error" : ""}">
        ${this.statusText()}
      </div>

      ${this.renderModelInfo()}
    `;
    }
    /* ── Legend ── */
    renderLegend() {
        const entries = Object.entries(config.classColors);
        return html `
      <div class="legend">
        ${entries.map(([className, color]) => html `
            <div class="legend-item">
              <div class="legend-dot" style="background:${color}"></div>
              ${this.classLabel(className)}
              <div class="legend-tooltip">${this.classTooltip(className)}</div>
            </div>
          `)}
      </div>
    `;
    }
    classLabel(className) {
        const info = classInfo[className] ?? unknownClassInfo;
        return info.label[this.uiLang];
    }
    classTooltip(className) {
        const info = classInfo[className] ?? unknownClassInfo;
        return info.tooltip[this.uiLang];
    }
    /* ── Stats ── */
    renderStats() {
        if (this.status.kind !== "done")
            return nothing;
        const filtered = this.filteredDetections();
        if (filtered.length === 0)
            return nothing;
        const avgConf = filtered.reduce((sum, d) => sum + d.confidence, 0) / filtered.length;
        return html `
      <div class="stats">
        <div class="stat">
          <div class="n">${filtered.length}</div>
          <div class="l">${this.uiLang === "en" ? "detections" : "rilevamenti"}</div>
        </div>
        <div class="stat">
          <div class="n">${Math.round(avgConf * 100)}%</div>
          <div class="l">${this.uiLang === "en" ? "avg confidence" : "confidenza media"}</div>
        </div>
        <div class="stat">
          <div class="n">${(this.status.result.elapsedMs / 1000).toFixed(1)}s</div>
          <div class="l">${this.uiLang === "en" ? "inference" : "inferenza"}</div>
        </div>
      </div>
    `;
    }
    /* ── Confidence slider ── */
    renderSlider() {
        if (this.status.kind !== "done")
            return nothing;
        return html `
      <div class="slider-row">
        <label>${this.uiLang === "en" ? "Confidence" : "Confidenza"}</label>
        <input
          type="range"
          min="1"
          max="99"
          step="1"
          .value=${String(this.confidenceThreshold)}
          @input=${this.onSliderInput}
        />
        <span class="slider-val">${this.confidenceThreshold}%</span>
      </div>
    `;
    }
    onSliderInput(e) {
        const val = parseInt(e.target.value, 10);
        this.confidenceThreshold = val;
        this.inspectedDetection = null;
        void this.reRenderOverlay();
    }
    /* ── Inspect panel ── */
    renderInspectPanel() {
        if (!this.inspectedDetection)
            return nothing;
        const det = this.inspectedDetection;
        const info = classInfo[det.class] ?? unknownClassInfo;
        const color = config.classColors[det.class] ??
            config.unknownClassColor;
        const pct = Math.round(det.confidence * 100);
        return html `
      <div
        class="inspect-panel"
        style="left:${this.inspectPos.x}px;top:${this.inspectPos.y}px"
      >
        <button class="inspect-close" @click=${this.closeInspect}>x</button>
        <div class="class-name" style="color:${color}">
          ${info.label[this.uiLang]} — ${pct}%
        </div>
        <div class="confidence-bar">
          <div
            class="confidence-fill"
            style="width:${pct}%;background:${color}"
          ></div>
        </div>
        <div class="desc">${info.tooltip[this.uiLang]}</div>
      </div>
    `;
    }
    onCanvasClick(e) {
        if (this.status.kind !== "done")
            return;
        const canvas = this.canvasEl;
        if (!canvas)
            return;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const cx = (e.clientX - rect.left) * scaleX;
        const cy = (e.clientY - rect.top) * scaleY;
        const filtered = this.filteredDetections();
        const hit = findDetectionAt(cx, cy, filtered);
        if (hit) {
            this.inspectedDetection = hit;
            this.inspectPos = {
                x: Math.min(e.clientX - rect.left, rect.width - 270),
                y: Math.max(0, e.clientY - rect.top - 60),
            };
        }
        else {
            this.inspectedDetection = null;
        }
    }
    closeInspect() {
        this.inspectedDetection = null;
    }
    /* ── Model info ── */
    renderModelInfo() {
        const rows = [
            [this.uiLang === "en" ? "Architecture" : "Architettura", "RF-DETR Medium"],
            [
                this.uiLang === "en" ? "Training images" : "Immagini di addestramento",
                "49 (v8)",
            ],
            [this.uiLang === "en" ? "Classes" : "Classi", "4 (1yr, 2yr, 3yr+, Support)"],
            ["mAP @ v8", "48.0%"],
            [
                this.uiLang === "en" ? "Best confidence" : "Miglior confidenza",
                "TBD (testing)",
            ],
            ["Hosting", "Roboflow (paid)"],
            [
                this.uiLang === "en" ? "Inference" : "Inferenza",
                this.uiLang === "en" ? "REST API + on-device" : "API REST + su dispositivo",
            ],
        ];
        return html `
      <details class="model-info">
        <summary>
          ${this.uiLang === "en" ? "Model info" : "Info modello"}
        </summary>
        <div class="model-detail">
          ${rows.map(([k, v]) => html `
              <div class="model-row">
                <span class="mk">${k}</span>
                <span class="mv">${v}</span>
              </div>
            `)}
        </div>
      </details>
    `;
    }
    /* ── Core logic ── */
    toggleLang() {
        this.uiLang = this.uiLang === "en" ? "it" : "en";
    }
    filteredDetections() {
        if (this.status.kind !== "done")
            return [];
        const threshold = this.confidenceThreshold / 100;
        return this.status.result.detections.filter((d) => d.confidence >= threshold);
    }
    captureButtonText() {
        const en = this.uiLang === "en";
        switch (this.status.kind) {
            case "idle":
                return en ? "Capture vine photo" : "Scatta foto della vite";
            case "preparing":
                return en ? "Preparing..." : "Preparazione...";
            case "inferring":
                return en ? "Analyzing..." : "Analisi in corso...";
            case "done":
                return en ? "Capture another" : "Scatta un'altra foto";
            case "error":
                return en ? "Try again" : "Riprova";
        }
    }
    statusText() {
        const en = this.uiLang === "en";
        switch (this.status.kind) {
            case "idle":
                return en
                    ? "Point at a vine, tap capture."
                    : "Inquadra una vite, premi scatta.";
            case "preparing":
                return en ? "Resizing image..." : "Ridimensionamento immagine...";
            case "inferring":
                return en ? "Running inference..." : "Inferenza in corso...";
            case "done": {
                const n = this.filteredDetections().length;
                const total = this.status.result.detections.length;
                if (n === total) {
                    return en
                        ? `${n} detections`
                        : `${n} rilevamenti`;
                }
                return en
                    ? `Showing ${n} of ${total} detections (${this.confidenceThreshold}% threshold)`
                    : `${n} di ${total} rilevamenti (soglia ${this.confidenceThreshold}%)`;
            }
            case "error":
                return this.status.message;
        }
    }
    async onFileChange(e) {
        const input = e.target;
        const file = input.files?.[0];
        if (!file)
            return;
        this.inspectedDetection = null;
        try {
            this.status = { kind: "preparing" };
            const prepared = await prepareImage(file);
            this.status = { kind: "inferring" };
            const result = await this.inference.detect(prepared.blob);
            this.lastImageBlob = prepared.blob;
            this.lastFullResult = result;
            this.confidenceThreshold = config.confidenceThreshold;
            await this.renderResult(prepared.blob, result);
            this.status = { kind: "done", result };
        }
        catch (err) {
            this.status = {
                kind: "error",
                message: err instanceof Error ? err.message : String(err),
            };
        }
        finally {
            input.value = "";
        }
    }
    async renderResult(imageBlob, result) {
        await this.updateComplete;
        const canvas = this.canvasEl;
        if (!canvas)
            return;
        const bitmap = await createImageBitmap(imageBlob);
        canvas.width = result.imageWidth;
        canvas.height = result.imageHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx)
            return;
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        bitmap.close();
        const threshold = this.confidenceThreshold / 100;
        const filtered = {
            ...result,
            detections: result.detections.filter((d) => d.confidence >= threshold),
        };
        drawOverlay(canvas, filtered);
    }
    async reRenderOverlay() {
        if (!this.lastImageBlob || !this.lastFullResult)
            return;
        await this.renderResult(this.lastImageBlob, this.lastFullResult);
    }
};
__decorate([
    state()
], PruneItApp.prototype, "status", void 0);
__decorate([
    state()
], PruneItApp.prototype, "uiLang", void 0);
__decorate([
    state()
], PruneItApp.prototype, "confidenceThreshold", void 0);
__decorate([
    state()
], PruneItApp.prototype, "inspectedDetection", void 0);
__decorate([
    state()
], PruneItApp.prototype, "inspectPos", void 0);
__decorate([
    query("canvas")
], PruneItApp.prototype, "canvasEl", void 0);
PruneItApp = __decorate([
    customElement("prune-it-app")
], PruneItApp);
export { PruneItApp };
